// Calculateur de remise arrière grossiste — logique pure, sans DOM ni réseau.
//
// Né le 25/09/2026 d'une vraie décision : quitter Pharmavance (Alliance) pour
// UniQ (CERP). Mehdi avait deux barèmes en tête et une intuition (« y a pas
// photo ») ; le calcul sur 12 mois de lignes Alliance a montré que la remise
// GROSSISTE était quasi neutre (−225 €/an sur 1,26 M€), le gain réel venant de la
// cotisation et du génériqueur. Ce module rend ce raisonnement rejouable pour
// n'importe quelle officine et n'importe quel couple de barèmes.
//
// CONTRAT D'ENTRÉE — les « lignes » sont des AGRÉGATS par prix de boîte, pas des
// lignes de facture (c'est l'action `remise_grossiste_base` qui les fabrique) :
//   { famille: "generique" | "princeps" | "hors_repertoire",
//     titulaire: string | null,   // renseigné pour les génériques seulement
//     pu: number,                 // prix HT net par boîte (le seuil s'y applique)
//     boites: number, ht: number, ht_brut: number,
//     remisee: boolean }          // ligne déjà remisée sur facture (depuis 2.15)
//
// CONTRAT D'UN BARÈME — des tranches de prix par boîte, bornes CROISSANTES,
// la dernière sans borne (`max: null`) :
//   { nom, exclureGeneriques: true,
//     tranches: [{ max: 450, mode: "pct", valeur: 4.75 },
//                { max: 1930, mode: "boite", valeur: 20 },
//                { max: null, mode: "rien" }] }
// Une boîte appartient à la PREMIÈRE tranche dont `max` est ≥ son prix : une
// boîte à 450 € pile est donc dans « jusqu'à 450 € ». C'est la lecture retenue
// le 25/09 ; si un grossiste compte « strictement inférieur », le barème le dira.

export const MODES = {
  pct: "% des achats",
  boite: "€ par boîte",
  rien: "aucune remise",
};

/**
 * Barèmes pré-remplis : ce sont des EXEMPLES réels, modifiables à l'écran.
 *
 * PROMESSE ≠ VÉRITÉ (mesuré le 30/09/2026 sur les 326 documents Alliance des
 * Théâtres, rapport ~/livrables/2026-09-30/theatres-remises-alliance/) : le 3,6 %
 * est un TAUX, pas un rendement. La remise arrière Alliance (« abn marge ») vaut
 * 3,6 % des achats nets hors génériques du mois PRÉCÉDENT les bons mois (23 sur
 * 52) ; les autres sont payés moins, puis rattrapés en partie par des
 * régularisations versées en banque (« REM COMM »), hors relevés. Sur 12 mois
 * (09/2025 → 08/2026) : 3,08 % sans les régularisations, 3,34 % avec la part qui
 * concerne ces 12 mois. ⚠️ Une régularisation ne compte que pour les mois qu'elle
 * couvre : celle du 17/06/2026 (7 576 €) couvre mars 2025 → avril-mai 2026, dont
 * 47 % seulement dans la fenêtre (prorata des manques ; 3,41 % au prorata du
 * temps). La compter entière (3,64 %, version 2.12) était une erreur. Ces deux
 * taux sont mesurés sur « nets des relevés − génériques du MDL Alliance ».
 *
 * ASSIETTE (2.15, règle constatée le 30/09/2026) : Alliance verse 3,8 % des
 * achats SANS remise sur facture du mois précédent, génériques non remisés
 * compris, produits remisés exclus. Mesuré à 3,79 % sur 18 mois pleins (tableaux
 * de bord Alliance) et sur 6 mois de lignes de factures des Théâtres. En euros,
 * c'est la même chose que « 3,6 % hors génériques du MDL » : taux et assiette vont
 * par paire. Les exemples ci-dessus sont donnés pour l'assiette « sans remise »
 * (Théâtres, 12 mois, lignes sans remise 1 271 791 € après correction de la
 * couverture : 41 408 € versés sur relevés = 3,26 % ; + 3 583 € de régularisation
 * rattachée = 3,54 %). CERP est supposé suivre le même barème (Mehdi, 30/09).
 *
 * `partGeneriques` (optionnel, %, assiette « hors génériques » seulement) : part des génériques du RÉFÉRENTIEL que ce
 * grossiste remise quand même. Cet écran retire les génériques du référentiel
 * (BDPM, types 1, 2, 4) ; Alliance n'en retire qu'une partie (la ligne
 * « GÉNÉRIQUES » de ses MDL, plus étroite, règle produit non reconstituée).
 * Mesuré le 30/09/2026 : Théâtres ≈ 38 % en euros (assiette Alliance 1 346 263 €
 * contre 1 258 100 € ici, génériques ≈ 232 k€) ; Cardinet ≈ 32 % en unités
 * (déc. 2025 → août 2026). C'est une moyenne, appliquée à chaque générique avec
 * la tranche de son prix de boîte. Absent ou vide = 0.
 *
 * `dividende` (optionnel, % de l'assiette remisée) : remise complémentaire
 * versée à part — ristourne de coopérative, dividende, prime de fin d'année.
 * Absent ou vide = 0 (une sauvegarde plus ancienne n'en a pas).
 */
export const BAREMES_EXEMPLES = {
  alliance: {
    nom: "Alliance promis (3,8 %)",
    exclureGeneriques: true,
    tranches: [{ max: null, mode: "pct", valeur: 3.8 }],
  },
  alliance_verse: {
    nom: "Alliance versé sur relevés (3,26 %, mesuré)",
    exclureGeneriques: true,
    tranches: [{ max: null, mode: "pct", valeur: 3.26 }],
  },
  alliance_regul: {
    nom: "Alliance avec régularisations (3,54 %, mesuré)",
    exclureGeneriques: true,
    tranches: [{ max: null, mode: "pct", valeur: 3.54 }],
  },
  cerp: {
    nom: "CERP via UniQ (proposé)",
    exclureGeneriques: true,
    tranches: [
      { max: 450, mode: "pct", valeur: 4.75 },
      { max: 1930, mode: "boite", valeur: 20 },
      { max: null, mode: "rien", valeur: 0 },
    ],
  },
};

/**
 * Génériqueurs pré-remplis. Structure d'un accord (Mehdi, 26/09, Biogaran) :
 *  • `rsf` — remise sur facture GARANTIE, sur tout le brut générique SAUF la part
 *    dont la remise sur facture est à 2,5 % (qui reste à 2,5 %) ; si la facture
 *    n'y est pas, la différence remonte en remise arrière ;
 *  • `ra` — remise arrière sur TOUT le brut, quelle que soit la remise de départ.
 * Biogaran : 33 % + 24 % aujourd'hui ; UniQ abonde la remise arrière de 4 points.
 * Sandoz, Viatris : conditions non connues au 26/09.
 */
export const GENERIQUEURS_EXEMPLES = [
  { titulaire: "BIOGARAN", rsf: { actuel: 33, propose: 33 }, ra: { actuel: 24, propose: 28 } },
  { titulaire: "SANDOZ", rsf: { actuel: null, propose: null }, ra: { actuel: null, propose: null } },
  { titulaire: "VIATRIS", rsf: { actuel: null, propose: null }, ra: { actuel: null, propose: null } },
];

/** Taux de la part « à faible remise sur facture » : elle garde le sien. */
export const TAUX_RSF_BAS = 2.5;

// ⚠️ `Number(null)` vaut 0 : sans le garde, un taux NON RENSEIGNÉ devenait 0 et
// l'écran annonçait « 0 € à gagner » au lieu de « inconnu » (attrapé par test).
const num = (v) => {
  if (v == null || (typeof v === "string" && v.trim() === "")) return null;
  const n = typeof v === "string" ? Number(v.replace(",", ".")) : Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Liste des problèmes d'un barème, vide s'il est utilisable.
 * On refuse plutôt que de « corriger » : un barème faux calcule un chiffre faux
 * qui a l'air juste, et ce chiffre part chez une consœur.
 */
export function problemesBareme(bareme) {
  const pb = [];
  const t = bareme && Array.isArray(bareme.tranches) ? bareme.tranches : [];
  if (t.length === 0) return ["Le barème n'a aucune tranche."];
  let precedent = 0;
  t.forEach((tr, i) => {
    const derniere = i === t.length - 1;
    const max = tr.max == null || tr.max === "" ? null : num(tr.max);
    if (derniere && max != null) pb.push("La dernière tranche doit rester sans limite (« au-delà »).");
    if (!derniere) {
      if (max == null) pb.push(`Tranche ${i + 1} : une limite de prix est nécessaire.`);
      else if (max <= precedent) pb.push(`Tranche ${i + 1} : les limites doivent être croissantes.`);
      else precedent = max;
    }
    if (!MODES[tr.mode]) pb.push(`Tranche ${i + 1} : type de remise inconnu.`);
    if (tr.mode !== "rien") {
      const v = num(tr.valeur);
      if (v == null || v < 0) pb.push(`Tranche ${i + 1} : valeur manquante ou négative.`);
      else if (tr.mode === "pct" && v > 100) pb.push(`Tranche ${i + 1} : un pourcentage ne dépasse pas 100.`);
    }
  });
  const d = bareme.dividende;
  if (!(d == null || (typeof d === "string" && d.trim() === ""))) {
    const v = num(d);
    if (v == null || v < 0 || v > 100) pb.push("Dividende : un pourcentage entre 0 et 100.");
  }
  const g = bareme.partGeneriques;
  if (!(g == null || (typeof g === "string" && g.trim() === ""))) {
    const v = num(g);
    if (v == null || v < 0 || v > 100) pb.push("Part des génériques remisée : un pourcentage entre 0 et 100.");
  }
  return pb;
}

/** La tranche d'un prix de boîte (première dont la limite couvre le prix). */
export function trancheDe(pu, tranches) {
  for (const tr of tranches) {
    if (tr.max == null || tr.max === "" || pu <= num(tr.max)) return tr;
  }
  return tranches[tranches.length - 1];
}

/** Remise produite par UN agrégat sous UNE tranche. Les retours (négatifs) la réduisent. */
export function remiseDe(ligne, tranche) {
  if (tranche.mode === "pct") return (ligne.ht * num(tranche.valeur)) / 100;
  if (tranche.mode === "boite") return ligne.boites * num(tranche.valeur);
  return 0;
}

const estRemisable = (ligne, bareme) => !(bareme.exclureGeneriques && ligne.famille === "generique");

/** Bornes communes aux deux barèmes, pour que chaque ligne du tableau n'ait qu'UNE règle de chaque côté. */
export function bornesCommunes(a, b) {
  const s = new Set();
  for (const tr of [...a.tranches, ...b.tranches]) {
    const m = tr.max == null || tr.max === "" ? null : num(tr.max);
    if (m != null) s.add(m);
  }
  return [...s].sort((x, y) => x - y);
}

export function libelleSegment(bas, haut) {
  const f = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(n) + " €";
  if (bas == null && haut == null) return "toutes les boîtes";
  if (bas == null) return `jusqu'à ${f(haut)}`;
  if (haut == null) return `au-delà de ${f(bas)}`;
  return `de ${f(bas)} à ${f(haut)}`;
}

/**
 * Compare deux barèmes sur les mêmes achats.
 * Rend un tableau par segment de prix (bornes des deux barèmes fusionnées) et
 * les totaux. `base` = achats HT réellement remisables (génériques exclus si
 * l'UN des barèmes les exclut — sinon les deux totaux ne portent pas sur la même
 * assiette, et l'écart mesurerait la définition, pas le barème).
 */
export const ASSIETTES = {
  sans_remise: "Lignes sans remise sur facture (règle Alliance constatée)",
  hors_generiques: "Tout sauf les génériques du référentiel",
};

export function comparerBaremes(lignes, actuel, propose, { assiette = "hors_generiques" } = {}) {
  const bornes = bornesCommunes(actuel, propose);
  const segments = [];
  let bas = null;
  for (const haut of [...bornes, null]) {
    segments.push({ bas, haut, libelle: libelleSegment(bas, haut), ht: 0, boites: 0, nbPrix: 0, actuel: 0, propose: 0 });
    bas = haut;
  }
  const segmentDe = (pu) => segments.find((s) => s.haut == null || pu <= s.haut);

  const exclure = { ...actuel, exclureGeneriques: actuel.exclureGeneriques || propose.exclureGeneriques };
  const sansRemise = assiette === "sans_remise";
  let exclusHt = 0;
  for (const l of lignes) {
    // « sans remise » : une ligne déjà remisée sur facture sort, générique ou pas.
    const dedans = sansRemise ? !l.remisee : estRemisable(l, exclure);
    if (!dedans) { exclusHt += l.ht; continue; }
    const s = segmentDe(l.pu);
    s.ht += l.ht;
    s.boites += l.boites;
    s.nbPrix += 1;
    s.actuel += remiseDe(l, trancheDe(l.pu, actuel.tranches));
    s.propose += remiseDe(l, trancheDe(l.pu, propose.tranches));
  }
  for (const s of segments) s.ecart = s.propose - s.actuel;

  // Génériques que CHAQUE grossiste remise quand même (sa définition est plus
  // étroite que le référentiel) : `partGeneriques` % de chaque ligne générique,
  // avec la tranche de son prix de boîte dans CE barème.
  // Les génériques ne sortent des tranches que si l'UN des barèmes les exclut ;
  // celui qui ne les exclut pas les remise alors à 100 %.
  const partDe = (b) => (b.exclureGeneriques ? (num(b.partGeneriques) ?? 0) / 100 : 1);
  const horsTranches = !sansRemise && exclure.exclureGeneriques;
  const generiques = { ht: exclusHt, partActuel: horsTranches ? partDe(actuel) : 0, partPropose: horsTranches ? partDe(propose) : 0, actuel: 0, propose: 0 };
  if (generiques.partActuel || generiques.partPropose) {
    for (const l of lignes) {
      if (l.famille !== "generique") continue;
      generiques.actuel += remiseDe(l, trancheDe(l.pu, actuel.tranches)) * generiques.partActuel;
      generiques.propose += remiseDe(l, trancheDe(l.pu, propose.tranches)) * generiques.partPropose;
    }
  }

  const somme = (k) => segments.reduce((t, s) => t + s[k], 0);
  // Dividende / remise complémentaire : un % de toute l'assiette remisée, versé
  // à part. Compté dans le total, pas dans les tranches (il n'en dépend pas).
  const base = somme("ht");
  const dividende = { actuel: (base * (num(actuel.dividende) ?? 0)) / 100, propose: (base * (num(propose.dividende) ?? 0)) / 100 };
  const totActuel = somme("actuel") + dividende.actuel + generiques.actuel;
  const totPropose = somme("propose") + dividende.propose + generiques.propose;
  return {
    segments: segments.filter((s) => s.nbPrix > 0),
    dividende,
    generiques,
    assiette: sansRemise ? "sans_remise" : "hors_generiques",
    total: { ht: base, boites: somme("boites"), actuel: totActuel, propose: totPropose, ecart: totPropose - totActuel },
    exclusHt,
  };
}

/**
 * Volet génériqueur, sur le prix BRUT (PFHT) facturé des boîtes du titulaire :
 *   remise = rsf × brut hors part 2,5 % + 2,5 % × part 2,5 % + ra × tout le brut.
 * Hypothèse affichée : toutes les boîtes du titulaire achetées au grossiste sont
 * couvertes par l'accord.
 * Un taux manquant rend la remise correspondante `null` — jamais 0, qui se
 * lirait comme « rien à gagner ».
 */
export function remiseGeneriqueur(brutHaut, brutBas, rsf, ra) {
  const r = num(rsf);
  const a = num(ra);
  if (r == null || a == null) return null;
  return (brutHaut * r) / 100 + (brutBas * TAUX_RSF_BAS) / 100 + ((brutHaut + brutBas) * a) / 100;
}

export function calculerGeneriqueurs(lignes, generiqueurs) {
  return generiqueurs.map((g) => {
    const cle = String(g.titulaire || "").trim().toUpperCase();
    let brutHaut = 0;
    let brutBas = 0;
    let boites = 0;
    if (cle) {
      for (const l of lignes) {
        if (l.famille !== "generique" || !l.titulaire) continue;
        if (!String(l.titulaire).toUpperCase().includes(cle)) continue;
        if (l.rsf_bas) brutBas += l.ht_brut;
        else brutHaut += l.ht_brut;
        boites += l.boites;
      }
    }
    const actuel = remiseGeneriqueur(brutHaut, brutBas, g.rsf?.actuel, g.ra?.actuel);
    const propose = remiseGeneriqueur(brutHaut, brutBas, g.rsf?.propose, g.ra?.propose);
    const gain = actuel == null || propose == null ? null : propose - actuel;
    return { titulaire: cle, baseBrut: brutHaut + brutBas, brutBas, boites, actuel, propose, gain };
  });
}

/** Les titulaires de génériques présents, triés par poids — pour proposer la liste à l'écran. */
export function titulairesGeneriques(lignes, limite = 8) {
  const m = new Map();
  for (const l of lignes) {
    if (l.famille !== "generique" || !l.titulaire) continue;
    m.set(l.titulaire, (m.get(l.titulaire) ?? 0) + l.ht_brut);
  }
  return [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, limite).map(([titulaire, baseBrut]) => ({ titulaire, baseBrut }));
}

/** Période par défaut : les 12 derniers mois COMPLETS avant `maintenant`. */
export function periodeParDefaut(maintenant = new Date()) {
  const fin = new Date(Date.UTC(maintenant.getFullYear(), maintenant.getMonth(), 0));
  const debut = new Date(Date.UTC(fin.getUTCFullYear(), fin.getUTCMonth() - 11, 1));
  const iso = (d) => d.toISOString().slice(0, 10);
  return { debut: iso(debut), fin: iso(fin) };
}
